import { IKeys } from '@/common/constants/ikeys'
import { tGlobal } from '@/common/composables/useI18n'
import type { Router } from 'vue-router'
import type { useUserStore } from '@/stores/user'
import type { getWebSocketService } from '@/services/websocket/websocket.service'

export interface ForcedLogoutToast {
  severity: 'warn' | 'info'
  summary: string
  detail?: string
  life: number
}

export interface ForcedLogoutDeps {
  userStore: ReturnType<typeof useUserStore>
  wsService: ReturnType<typeof getWebSocketService>
  router: Router
  notify: (toast: ForcedLogoutToast) => void
}

let forcedLogoutInProgress = false

const readTypedFieldValue = (entry: unknown): unknown => {
  if (entry != null && typeof entry === 'object' && 'value' in (entry as Record<string, unknown>)) {
    return (entry as { value: unknown }).value
  }
  return entry
}

export function isForcedLogoutEventType(type: unknown): boolean {
  const normalized = String(type ?? '')
  return normalized === IKeys.$LOGOUT || normalized === IKeys.CHANGE_PASSWORD_LOGOUT
}

export function extractForcedLogoutNotification(
  eventData: Record<string, unknown> | null | undefined,
): string {
  if (!eventData || typeof eventData !== 'object') {
    return ''
  }
  const value = readTypedFieldValue(eventData.notification)
  return value === undefined || value === null ? '' : String(value)
}

export function resolveForcedLogoutMessages(
  eventType: string,
  notification: string,
): ForcedLogoutToast | null {
  const t = tGlobal

  if (eventType === IKeys.$LOGOUT) {
    const toast: ForcedLogoutToast = {
      severity: 'warn',
      summary: t('platform.forcedLogout.title'),
      life: 5000,
    }
    if (notification) {
      toast.detail = notification
    }
    return toast
  }

  if (eventType === IKeys.CHANGE_PASSWORD_LOGOUT) {
    return {
      severity: 'info',
      summary: notification || t('platform.changePasswordLogout.title'),
      detail: t('platform.changePasswordLogout.message'),
      life: 5000,
    }
  }

  return null
}

export function resetForcedLogoutGuardForTests(): void {
  forcedLogoutInProgress = false
}

export async function handleForcedLogoutSystemEvent(
  eventData: Record<string, unknown> | null | undefined,
  eventType: string,
  deps: ForcedLogoutDeps,
): Promise<boolean> {
  if (!isForcedLogoutEventType(eventType)) {
    return false
  }

  if (forcedLogoutInProgress) {
    return false
  }
  forcedLogoutInProgress = true

  try {
    const notification = extractForcedLogoutNotification(eventData)
    const toast = resolveForcedLogoutMessages(eventType, notification)
    if (toast) {
      deps.notify(toast)
    }

    deps.userStore.logout()
    deps.wsService.disconnect()
    await deps.router.push({ name: 'signin' })
    return true
  } finally {
    forcedLogoutInProgress = false
  }
}
